import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import './i18n';
import './App.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionsProvider, usePermissions } from './context/PermissionsContext';
import { NotificationProvider } from './context/NotificationContext';
import { Layout } from './components/Layout';
import { SuperAdminLayout } from './components/SuperAdminLayout';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import IssuesPage from './pages/IssuesPage';
import NewIssuePage from './pages/NewIssuePage';
import IssueDetailPage from './pages/IssueDetailPage';
import IssueConversationPage from './pages/IssueConversationPage';
import PropertiesPage from './pages/PropertiesPage';
import ApartmentsPage from './pages/ApartmentsPage';
import UsersPage from './pages/UsersPage';
import SuppliersPage from './pages/SuppliersPage';
import OrdersPage from './pages/OrdersPage';
import OrderConfirmPage from './pages/OrderConfirmPage';
import SettingsPage from './pages/SettingsPage';
import EmailThreadsPage from './pages/EmailThreadsPage';
import ServiceMessagesPage from './pages/ServiceMessagesPage';
import DocumentsPage from './pages/DocumentsPage';
import HistoryPage from './pages/HistoryPage';
import MaintenancePage from './pages/MaintenancePage';
import ProtocolsPage from './pages/ProtocolsPage';
import ExternalIssueForm from './pages/ExternalIssueForm';
import EmbedFormPage from './pages/EmbedFormPage';
import TrackIssuePage from './pages/TrackIssuePage';
import EconomyPage from './pages/EconomyPage';
import AnnualReportPage from './pages/AnnualReportPage';
import InvoicingPage from './pages/InvoicingPage';
import ErrorBoundary from './components/ErrorBoundary';

// Superadmin Pages
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import OrganizationsPage from './pages/OrganizationsPage';
import OrganizationDetailPage from './pages/OrganizationDetailPage';
import NewOrganizationPage from './pages/NewOrganizationPage';
import SuperAdminEmailsPage from './pages/SuperAdminEmailsPage';
import SuperAdminSettingsPage from './pages/SuperAdminSettingsPage';
import SuperAdminUsersPage from './pages/SuperAdminUsersPage';
import PermissionsPage from './pages/PermissionsPage';
import AuditLogPage from './pages/AuditLogPage';

// Subcontractor Pages
import SubcontractorLoginPage from './pages/SubcontractorLoginPage';
import SubcontractorOrdersPage from './pages/SubcontractorOrdersPage';
import SubcontractorOrderDetailPage from './pages/SubcontractorOrderDetailPage';

// Portal Pages
import PortalPage from './pages/PortalPage';
import MemberUpdatePage from './pages/MemberUpdatePage';
import MailPortal from './pages/MailPortal';
import PortalAdminPage from './pages/PortalAdminPage';
import PortalContentPage from './pages/PortalContentPage';
import StandalonePortal from './pages/StandalonePortal';

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
  requiredPermission?: string | null;
}

// Protected route wrapper
function ProtectedRoute({ children, adminOnly = false, requiredPermission = null }: ProtectedRouteProps) {
  const { isAuthenticated, isAdmin, user, loading } = useAuth();
  const { hasPermission, loading: permLoading } = usePermissions();
  
  if (loading || permLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  // Superadmin has access to everything - don't redirect them automatically
  // They can access both /superadmin and regular /dashboard
  const isSuperAdmin = user?.role === 'superadmin';
  
  // Check permission if specified (superadmin bypasses all permission checks)
  if (requiredPermission && !isSuperAdmin && !hasPermission(requiredPermission)) {
    return (
      <Layout>
        <div className="p-8 text-center">
          <h2 className="text-xl font-semibold text-slate-900">Åtkomst nekad</h2>
          <p className="text-slate-500 mt-2">Du har inte behörighet att se denna sida.</p>
        </div>
      </Layout>
    );
  }
  
  if (adminOnly && !isAdmin && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Layout>{children}</Layout>;
}

interface SuperAdminRouteProps {
  children: React.ReactNode;
}

// Superadmin route wrapper
function SuperAdminRoute({ children }: SuperAdminRouteProps) {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  if (user?.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <SuperAdminLayout>{children}</SuperAdminLayout>;
}

interface AuthRouteProps {
  children: React.ReactNode;
}

// Wrapper for StandalonePortal to get slug from URL params
function StandalonePortalWrapper() {
  const { slug } = useParams<{ slug: string }>();
  return <StandalonePortal slug={slug || ''} />;
}

// Auth route (redirect if logged in)
function AuthRoute({ children }: AuthRouteProps) {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    // Redirect superadmin to superadmin dashboard
    if (user?.role === 'superadmin') {
      return <Navigate to="/superadmin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <PermissionsProvider>
            <NotificationProvider>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<AuthRoute><LoginPage /></AuthRoute>} />
                <Route path="/order-confirm/:token" element={<OrderConfirmPage />} />
                <Route path="/report" element={<ExternalIssueForm />} />
                <Route path="/track" element={<TrackIssuePage />} />
                
                {/* Subcontractor routes (separate portal) */}
                <Route path="/subcontractor/login" element={<SubcontractorLoginPage />} />
                <Route path="/subcontractor/orders" element={<SubcontractorOrdersPage />} />
                <Route path="/subcontractor/orders/:orderId" element={<SubcontractorOrderDetailPage />} />
                
                {/* BRF Portal routes (public + authenticated) */}
                <Route path="/portal/:slug" element={<PortalPage />} />
                <Route path="/portal/:slug/update" element={<MemberUpdatePage />} />
                
                {/* Standalone member portal (public) */}
                <Route path="/medlem/:slug" element={<StandalonePortalWrapper />} />
                
                {/* Mail Portal - board members and admins with board_email permission */}
                <Route path="/mail/*" element={<ProtectedRoute requiredPermission="board_email"><MailPortal /></ProtectedRoute>} />
            
            {/* Superadmin routes */}
            <Route path="/superadmin" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />
            <Route path="/superadmin/organizations" element={<SuperAdminRoute><OrganizationsPage /></SuperAdminRoute>} />
            <Route path="/superadmin/organizations/new" element={<SuperAdminRoute><NewOrganizationPage /></SuperAdminRoute>} />
            <Route path="/superadmin/organizations/:id" element={<SuperAdminRoute><OrganizationDetailPage /></SuperAdminRoute>} />
            <Route path="/superadmin/users" element={<SuperAdminRoute><SuperAdminUsersPage /></SuperAdminRoute>} />
            <Route path="/superadmin/permissions" element={<SuperAdminRoute><PermissionsPage /></SuperAdminRoute>} />
            <Route path="/superadmin/emails" element={<SuperAdminRoute><SuperAdminEmailsPage /></SuperAdminRoute>} />
            <Route path="/superadmin/email" element={<SuperAdminRoute><EmailThreadsPage /></SuperAdminRoute>} />
            <Route path="/superadmin/audit" element={<SuperAdminRoute><AuditLogPage /></SuperAdminRoute>} />
            <Route path="/superadmin/settings" element={<SuperAdminRoute><SuperAdminSettingsPage /></SuperAdminRoute>} />
            <Route path="/superadmin/issues" element={<SuperAdminRoute><IssuesPage /></SuperAdminRoute>} />
            <Route path="/superadmin/issues/:id" element={<SuperAdminRoute><IssueDetailPage /></SuperAdminRoute>} />
            
            {/* Public routes - no auth required */}
            <Route path="/orders/confirm/:token" element={<OrderConfirmPage />} />
          
            {/* Protected routes with permission checks */}
            <Route path="/dashboard" element={<ProtectedRoute requiredPermission="dashboard"><DashboardPage /></ProtectedRoute>} />
            <Route path="/issues" element={<ProtectedRoute requiredPermission="issues_view"><IssuesPage /></ProtectedRoute>} />
            <Route path="/issues/new" element={<ProtectedRoute requiredPermission="issues_create"><NewIssuePage /></ProtectedRoute>} />
            <Route path="/issues/:id" element={<ProtectedRoute requiredPermission="issues_view"><IssueDetailPage /></ProtectedRoute>} />
            <Route path="/issues/:issueId/conversation" element={<ProtectedRoute requiredPermission="issues_manage"><IssueConversationPage /></ProtectedRoute>} />
            
            {/* Admin routes with permission checks */}
            <Route path="/email" element={<ProtectedRoute requiredPermission="email"><EmailThreadsPage /></ProtectedRoute>} />
            <Route path="/properties" element={<ProtectedRoute requiredPermission="properties"><PropertiesPage /></ProtectedRoute>} />
            <Route path="/apartments" element={<ProtectedRoute requiredPermission="apartments"><ApartmentsPage /></ProtectedRoute>} />
            <Route path="/residents" element={<ProtectedRoute requiredPermission="users_view"><UsersPage /></ProtectedRoute>} />
            <Route path="/suppliers" element={<ProtectedRoute requiredPermission="suppliers"><SuppliersPage /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute requiredPermission="orders"><OrdersPage /></ProtectedRoute>} />
            <Route path="/service-messages" element={<ProtectedRoute requiredPermission="service_messages"><ServiceMessagesPage /></ProtectedRoute>} />
            <Route path="/documents" element={<ProtectedRoute requiredPermission="dashboard"><DocumentsPage /></ProtectedRoute>} />
            <Route path="/history" element={<ProtectedRoute requiredPermission="users_view"><HistoryPage /></ProtectedRoute>} />
            <Route path="/maintenance" element={<ProtectedRoute requiredPermission="properties"><MaintenancePage /></ProtectedRoute>} />
            <Route path="/protocols" element={<ProtectedRoute requiredPermission="properties"><ProtocolsPage /></ProtectedRoute>} />
            <Route path="/economy" element={<ProtectedRoute requiredPermission="properties"><EconomyPage /></ProtectedRoute>} />
            <Route path="/invoicing" element={<ProtectedRoute requiredPermission="properties"><InvoicingPage /></ProtectedRoute>} />
            <Route path="/annual-report" element={<ProtectedRoute requiredPermission="properties"><AnnualReportPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute requiredPermission="settings"><SettingsPage /></ProtectedRoute>} />
            <Route path="/portal-admin" element={<ProtectedRoute requiredPermission="settings"><PortalAdminPage /></ProtectedRoute>} />
            <Route path="/portal-content" element={<ProtectedRoute requiredPermission="settings"><PortalContentPage /></ProtectedRoute>} />
            <Route path="/embed-form" element={<ProtectedRoute requiredPermission="settings"><EmbedFormPage /></ProtectedRoute>} />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                fontFamily: 'DM Sans, sans-serif',
              },
            }}
          />
            </NotificationProvider>
          </PermissionsProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
